/**
 * Collects every mark-as-completed blocker for the single-order footer into a
 * list of human-readable messages.
 *
 * The layout already computes each blocker boolean; this hook only maps them
 * to copy so the disabled state and the footer tooltip always agree.
 *
 * @module useSubmitValidationMessages
 */
import { useMemo } from "react";
import { MarkCompleteValidationMessages } from "../../../constants/markCompleteValidationMessages";

interface UseSubmitValidationMessagesParams {
  /** Review button is visible but the order has no review documents yet. */
  shouldShowReviewButton: boolean;
  /** Whether the order has at least one review document. */
  hasReviewDocuments: boolean;
  /** Any auth-on-file J-code has a `not_available` status. */
  hasJCodeNotAvailable: boolean;
  /** Any auth-on-file J-code is missing a letter or its review is not yet done. */
  authLetterReviewBlocksMarkComplete: boolean;
  /** More specific copy while a possible fax review is loading or unavailable. */
  authLetterReviewBlockerMessage?: MarkCompleteValidationMessages;
  /** Any J-code's CPT-table values differ from its auth-letter values. */
  authLetterFieldMismatchBlocksMarkComplete: boolean;
  /** Primary Dx (ICD-10) validation result for the order. */
  isPrimaryDiagnosisValidated: boolean;
  /** Facility requires a site NPI type and the order has none. */
  isSiteNpiValidationNeeded: boolean;
  /** CPT table has unsaved changes. */
  hasCptUnsavedChanges: boolean;
}

export interface SubmitValidationMessages {
  /** One message per failing validation, in stable display order. */
  validationMessages: string[];
  /** True when at least one validation is failing. */
  isSubmitDisabled: boolean;
}

/**
 * Maps the footer's mark-as-completed blockers to tooltip messages.
 *
 * @returns The failing validations as messages plus the combined disabled flag.
 */
export const useSubmitValidationMessages = ({
  shouldShowReviewButton,
  hasReviewDocuments,
  hasJCodeNotAvailable,
  authLetterReviewBlocksMarkComplete,
  authLetterReviewBlockerMessage,
  authLetterFieldMismatchBlocksMarkComplete,
  isPrimaryDiagnosisValidated,
  isSiteNpiValidationNeeded,
  hasCptUnsavedChanges,
}: UseSubmitValidationMessagesParams): SubmitValidationMessages => {
  const validationMessages = useMemo(() => {
    const messages: string[] = [];
    if (shouldShowReviewButton && !hasReviewDocuments) {
      messages.push(MarkCompleteValidationMessages.REVIEW_DOCUMENTS_REQUIRED);
    }
    if (hasJCodeNotAvailable) {
      messages.push(MarkCompleteValidationMessages.JCODE_NOT_AVAILABLE);
    }
    if (authLetterReviewBlocksMarkComplete) {
      messages.push(
        authLetterReviewBlockerMessage ??
          MarkCompleteValidationMessages.AUTH_LETTER_REVIEW_PENDING,
      );
    }
    if (authLetterFieldMismatchBlocksMarkComplete) {
      messages.push(MarkCompleteValidationMessages.AUTH_LETTER_FIELD_MISMATCH);
    }
    if (!isPrimaryDiagnosisValidated) {
      messages.push(
        MarkCompleteValidationMessages.PRIMARY_DIAGNOSIS_NOT_VALIDATED,
      );
    }
    if (isSiteNpiValidationNeeded) {
      messages.push(MarkCompleteValidationMessages.SITE_NPI_REQUIRED);
    }
    if (hasCptUnsavedChanges) {
      messages.push(MarkCompleteValidationMessages.CPT_UNSAVED_CHANGES);
    }
    return messages;
  }, [
    shouldShowReviewButton,
    hasReviewDocuments,
    hasJCodeNotAvailable,
    authLetterReviewBlocksMarkComplete,
    authLetterReviewBlockerMessage,
    authLetterFieldMismatchBlocksMarkComplete,
    isPrimaryDiagnosisValidated,
    isSiteNpiValidationNeeded,
    hasCptUnsavedChanges,
  ]);

  return {
    validationMessages,
    isSubmitDisabled: validationMessages.length > 0,
  };
};
